/**
 * «📈 Сравнительные тренды» — `.section-title-row` + `#trendsGrid` (ui-blocks.md §6.4).
 *
 * Особый случай (DECISIONS.md, 2026-09-07): в макете это пять скалярных
 * плиток без графика, ТЗ §8.2 требует линии. Делаем и то, и другое —
 * плитки макета остаются как есть, под ними линейный график Recharts
 * (поступило / закрыто / просрочено, предыдущий период пунктиром).
 *
 * Стрелка и цвет независимы: `move` задаёт направление, `gain` — цвет,
 * поэтому рост числа обращений даёт красную ▲.
 */

import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { ExportButton } from './ExportButton';
import { BLOCK_GAP, HEAD_NOTE_SM, TITLE_ROW_ACTIONS, dayWord } from './format';
import type { DashboardRange, PeriodValue, ShowToast, TrendsBlockDto } from './types';

interface Props {
    trends: TrendsBlockDto;
    range: DashboardRange;
    period: PeriodValue;
    /** Кнопку выгрузки видит только главврач. */
    canExport: boolean;
    toast: ShowToast;
}

/**
 * Порядок линий в легенде и подсказке — как линии объявлены ниже. Recharts 3 по умолчанию сортирует легенду
 * и подсказку по алфавиту, Recharts 2 не сортировал. Слой доступности (фокус по Tab, стрелки) в Recharts 3
 * включён по умолчанию — у графиков он выключен, как было в Recharts 2.
 */
const SERIES_ORDER = ['received', 'closed', 'overdue', 'prev_received', 'prev_closed', 'prev_overdue'];
const seriesRank = (item: { dataKey?: unknown }): number => SERIES_ORDER.indexOf(String(item.dataKey));

function arrowOf(move: number): string {
    if (Math.abs(move) < 0.05) return '➡';
    return move > 0 ? '▲' : '▼';
}

function toneOf(gain: number): 'flat' | 'good' | 'bad' {
    if (Math.abs(gain) < 0.05) return 'flat';
    return gain > 0 ? 'good' : 'bad';
}

export function TrendsBlock({ trends, range, period, canExport, toast }: Props) {
    const bucketNote =
        trends.bucket_days > 1
            ? `шаг ${trends.bucket_days} ${dayWord(trends.bucket_days)}`
            : 'по дням';

    return (
        <>
            <div className="section-title-row">
                <h2>📈 Сравнительные тренды</h2>
                <span style={TITLE_ROW_ACTIONS}>
                    <span className="pill" id="trendPill">
                        {range.label} · сравнение с предыдущим периодом
                    </span>
                    {canExport && <ExportButton period={period} toast={toast} />}
                </span>
            </div>

            <div className="trends-grid" id="trendsGrid">
                {trends.metrics.map((metric) => {
                    const tone = toneOf(metric.gain);
                    return (
                        <div className="trend-item" key={metric.key}>
                            <div className="trend-top">
                                <span className="trend-label">{metric.label}</span>
                                <span className={`trend-arrow ${tone}`}>{arrowOf(metric.move)}</span>
                            </div>
                            <div className="trend-value">{metric.value_text}</div>
                            <div className="trend-sub">
                                {metric.prev_text}
                                {' · '}
                                {tone === 'flat' ? (
                                    <span className="flat-text">без изменений</span>
                                ) : (
                                    <span className={tone === 'good' ? 'good-text' : 'bad-text'}>
                                        {tone === 'good' ? metric.good : metric.bad}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="stat-block" style={{ ...BLOCK_GAP, marginTop: 18 }}>
                <div className="block-header">
                    <span>📉 Динамика: поступило, закрыто, просрочено</span>
                    <span style={HEAD_NOTE_SM}>{bucketNote}</span>
                </div>
                <div className="block-body">
                    {trends.points.length === 0 ? (
                        <div className="detail-empty">За выбранный период данных нет — выберите период длиннее.</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={220}>
                            <LineChart
                                accessibilityLayer={false}
                                data={trends.points}
                                margin={{ top: 10, right: 12, bottom: 0, left: -18 }}
                            >
                                <CartesianGrid stroke="#e6ecf5" vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fill: '#56697f', fontSize: 13 }}
                                    stroke="#dce3ed"
                                />
                                <YAxis
                                    allowDecimals={false}
                                    tick={{ fill: '#56697f', fontSize: 13 }}
                                    stroke="#dce3ed"
                                />
                                <Tooltip
                                    itemSorter={seriesRank}
                                    contentStyle={{
                                        borderRadius: 10,
                                        border: '1px solid #dce3ed',
                                        fontSize: 14,
                                    }}
                                />
                                <Legend itemSorter={seriesRank} wrapperStyle={{ fontSize: 13 }} formatter={(value) => <span style={{ color: '#4d627a' }}>{value}</span>} />
                                <Line
                                    type="monotone"
                                    dataKey="received"
                                    name="Поступило"
                                    stroke="#1a5a8a"
                                    strokeWidth={2}
                                    dot={false}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="closed"
                                    name="Закрыто"
                                    stroke="#1f7a4d"
                                    strokeWidth={2}
                                    dot={false}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="overdue"
                                    name="Просрочено"
                                    stroke="#b22234"
                                    strokeWidth={2}
                                    dot={false}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="prev_received"
                                    name="Поступило · пред. период"
                                    stroke="#7ba3c8"
                                    strokeWidth={2}
                                    strokeDasharray="5 4"
                                    dot={false}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="prev_closed"
                                    name="Закрыто · пред. период"
                                    stroke="#8fc0a5"
                                    strokeWidth={2}
                                    strokeDasharray="5 4"
                                    dot={false}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="prev_overdue"
                                    name="Просрочено · пред. период"
                                    stroke="#d3919a"
                                    strokeWidth={2}
                                    strokeDasharray="5 4"
                                    dot={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
        </>
    );
}
